import { useState, useEffect } from 'react';
import { Loader2, Clock, CheckCircle2, XCircle, CalendarDays } from 'lucide-react';
import client from '../../api/client';
import { formatDateSafe } from '../../utils/formatDateSafe';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';

// ⚰ 'personal' retirado en V59 (fuera del CHECK de pto_requests).
const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad' };
const TYPE_COLORS = {
  vacation: { bg: '#dbeafe', color: '#1e40af' },
  sick:     { bg: '#fee2e2', color: '#991b1b' },
};

// V62 — la cadena son DOS niveles y se nombran por ROL: "L3" no le dice nada a nadie.
// ⚠ ANTI-DIVERGENCIA: espeja `PTO_LEVELS` del backend (ptoApprovalChainService).
const LEVEL_LABELS = { 1: 'Ingeniero(a)', 2: 'Socio(a)' };

// ⚠ V46 — `start_date` es DATE; el parseo ingenuo la corría un día al oeste de UTC.
const formatDate = (d) => formatDateSafe(d, { month: 'short', day: 'numeric' });

export default function AdminPtoStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/vacation/stats')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={18} className="spin" /> Cargando estadísticas…
      </div>
    );
  }

  if (!stats) return null;

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔴 V65 — ESTE ARCHIVO LEÍA UN CONTRATO QUE EL BACKEND YA NO SERVÍA.
  // ═══════════════════════════════════════════════════════════════════════════
  // Desestructuraba `{ counts, pendingByLevel, avgApprovalHours, byType }` en camelCase;
  // `getPtoStats` devuelve `by_status / pending_by_level / avg_approval_hours / by_type`
  // en snake_case desde V60 (f0282e1), y `counts` NO EXISTE — hay un ARREGLO `by_status`.
  // Resultado: `counts.pending` sobre `undefined` tumbaba la pestaña Vacaciones de /admin
  // ENTERA, y con ella la pantalla, desde V60.
  //
  // ⚠ V62 tocó este archivo y no lo vio: actualizó los RÓTULOS a la cadena de dos niveles
  // (LEVEL_LABELS, sacar 'personal') sin tocar la lectura. Un cambio cosmético sobre un
  // archivo roto lo hace parecer mantenido.
  //
  // ⚠ Y no era una clave: eran CUATRO. `by_type` trae `hours` y esto leía `total_days`
  // (→ "NaN días"), y `upcoming` trae `user_name` y esto leía `employee_name` (→ vacío).
  // Los dos habrían pasado desapercibidos porque no explotan: muestran basura.
  const counts = Object.fromEntries((stats.by_status || []).map(r => [r.status, r.count]));
  const pendingByLevel  = stats.pending_by_level || [];
  const avgApprovalHours = stats.avg_approval_hours;
  const byType   = stats.by_type || [];
  const upcoming = stats.upcoming || [];

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Summary stat cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatBox icon={Clock} color="#eab308" bg="#fef9c3" label="Pendientes" value={counts.pending ?? 0} />
        <StatBox icon={CheckCircle2} color="#16a34a" bg="#dcfce7" label="Aprobadas" value={counts.approved ?? 0} />
        <StatBox icon={XCircle} color="#dc2626" bg="#fee2e2" label="Denegadas" value={counts.denied ?? 0} />
        {avgApprovalHours != null && (
          <StatBox icon={CalendarDays} color="#6d28d9" bg="#ede9fe" label="Tiempo Prom." value={`${avgApprovalHours}h`} />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {/* Pending by level */}
        {pendingByLevel.length > 0 && (
          <div style={{ ...bubbleStyle, padding: '14px 18px' }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
              Pendientes por Nivel
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {pendingByLevel.map(row => (
                <div key={row.level} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: '#ede9fe', color: '#6d28d9',
                  }}>
                    {LEVEL_LABELS[row.level] || `L${row.level}`}
                  </span>
                  <span style={{ fontWeight: 600, color: NAVY }}>{parseInt(row.count)}</span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>solicitud{parseInt(row.count) !== 1 ? 'es' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By type */}
        {byType.length > 0 && (
          <div style={{ ...bubbleStyle, padding: '14px 18px' }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
              Por Tipo
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {byType.map(row => {
                const tc = TYPE_COLORS[row.type] || { bg: '#f1f5f9', color: '#64748b' };
                return (
                  <div key={row.type} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: tc.bg, color: tc.color,
                    }}>
                      {TYPE_LABELS[row.type] || row.type}
                    </span>
                    <span style={{ fontWeight: 600, color: NAVY }}>{parseInt(row.count)}</span>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>({parseFloat(row.hours)} horas)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming approved */}
        {upcoming.length > 0 && (
          <div style={{ ...bubbleStyle, padding: '14px 18px' }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
              Próximas Ausencias (30 días)
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
              {upcoming.map(r => {
                const tc = TYPE_COLORS[r.type] || { bg: '#f1f5f9', color: '#64748b' };
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: NAVY, minWidth: 100 }}>{r.user_name}</span>
                    <span style={{
                      padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: tc.bg, color: tc.color,
                    }}>
                      {TYPE_LABELS[r.type] || r.type}
                    </span>
                    <span style={{ color: '#64748b' }}>
                      {formatDate(r.start_date)}–{formatDate(r.end_date)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, color, bg, label, value }) {
  return (
    <div style={{
      ...bubbleStyle, padding: '12px 18px', flex: '1 1 140px', minWidth: 140,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: NAVY }}>{value}</div>
        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}
